import React, { useState, useCallback, useEffect } from "react";
import { useSession } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { Button } from "components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { Loader2 } from "lucide-react";
import * as Sentry from "@sentry/react";

const LANGUAGES = [
  { code: "nl", label: "Nederlands" },
  { code: "fr", label: "Frans" },
  { code: "en", label: "Engels" },
  { code: "tr", label: "Turks" },
  { code: "uk", label: "Oekraïens" },
  { code: "ary", label: "Arabisch" },
  { code: "pl", label: "Pools" },
  { code: "es", label: "Spaans" },
  { code: "de", label: "Duits" },
  { code: "ro", label: "Roemeens" },
  { code: "ru", label: "Russisch" },
] as const;

interface TranslatePreviewProps {
  htmlContent: string;
  bodyClassName?: string;
}

export const TranslatePreview: React.FC<TranslatePreviewProps> = ({
  htmlContent,
  bodyClassName,
}) => {
  const { session } = useSession();
  const [targetLanguage, setTargetLanguage] = useState<string>("nl");
  const [translatedHtml, setTranslatedHtml] = useState<string | null>(null);
  const [isTranslating, setIsTranslating] = useState(false);
  const [activeLanguage, setActiveLanguage] = useState<string | null>(null);

  // Reset translation when original content changes (different email selected)
  useEffect(() => {
    setTranslatedHtml(null);
    setActiveLanguage(null);
  }, [htmlContent]);

  const handleTranslate = useCallback(async () => {
    if (!htmlContent.trim() || !targetLanguage) return;

    setIsTranslating(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/mail-templates/translate`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            htmlContent,
            targetLanguages: [targetLanguage],
          }),
        },
        session,
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `Vertaling mislukt (${response.status})`,
        );
      }

      const data = await response.json();
      setTranslatedHtml(data.translations[targetLanguage]);
      setActiveLanguage(targetLanguage);
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: "email-translate-preview" },
        extra: { targetLanguage },
      });
      console.error("Vertaling mislukt:", error);
    } finally {
      setIsTranslating(false);
    }
  }, [htmlContent, targetLanguage, session]);

  const handleClear = useCallback(() => {
    setTranslatedHtml(null);
    setActiveLanguage(null);
  }, []);

  const selectedLabel =
    LANGUAGES.find((l) => l.code === targetLanguage)?.label || "";
  const displayHtml = translatedHtml ?? htmlContent;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={targetLanguage} onValueChange={setTargetLanguage}>
          <SelectTrigger className="h-8 w-[160px] text-sm">
            <SelectValue placeholder="Taal kiezen" />
          </SelectTrigger>
          <SelectContent
            position="popper"
            sideOffset={4}
            align="start"
            className="max-h-[200px]"
          >
            {LANGUAGES.map(({ code, label }) => (
              <SelectItem key={code} value={code}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button
          onClick={handleTranslate}
          disabled={!htmlContent.trim() || isTranslating}
          size="sm"
          variant="outline"
        >
          {isTranslating ? (
            <>
              <Loader2 className="mr-2 h-3 w-3 animate-spin" />
              Vertalen...
            </>
          ) : (
            `Vertaal naar ${selectedLabel}`
          )}
        </Button>

        {translatedHtml && (
          <Button onClick={handleClear} size="sm" variant="ghost">
            Toon origineel
          </Button>
        )}
      </div>

      {translatedHtml && activeLanguage && (
        <p className="text-neutral-secondary-mid text-xs italic">
          Vertaald naar{" "}
          {LANGUAGES.find((l) => l.code === activeLanguage)?.label} —
          automatische vertaling
        </p>
      )}

      <div
        className={bodyClassName}
        dangerouslySetInnerHTML={{ __html: displayHtml }}
      />
    </div>
  );
};
